import React, { useEffect, useState } from 'react';

import { link } from '@aline/routes';
import { Collapse, IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import { ChevronDown, ChevronUp, DownloadIcon, File, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip, Spacer } from 'src/components';
import { WorkflowReadyOnlyContext } from 'src/components/workflow/workflow-builder/WorkflowBuilderWorkflow';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { useExportWorkflowFiles } from 'src/hooks/workflow/workflow';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { getIsWorkflowStepComplete, ManageRecipientsSection } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

import { DeletePhaseButton } from './PhaseCard';
import { PhaseHomeColumnChip } from './PhaseHomeColumnChip';
import { PhaseStatusIndicator } from './PhaseStatusIndicator';
import { SigningBlockingIssuesAlert } from './SigningBlockingIssuesAlert';
import { SigningProgressStepper } from './SigningProgressStepper';

const useStyles = makeStyles()(theme => ({
  activeRoot: {
    border: `2px solid ${theme.appColors.primary ?? theme.palette.primary.main}`,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2, 2),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  headerActions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  root: {
    backgroundColor: theme.palette.common.white,
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    overflow: 'hidden',
    width: '100%',
  },
}));

interface SigningPhaseCardProps {
  boardId: string;
  phase: ApolloTypes.PhaseSimple_SigningPhase_Fragment;
  workflowColumnCategory: Apollo.ColumnCategory;
  workflowId: UUID;
  isActive?: boolean;
  readOnly?: boolean;
}

export const SigningPhaseCard = React.memo(function SigningPhaseCard({
  boardId,
  isActive = false,
  phase,
  readOnly = false,
  workflowColumnCategory,
  workflowId,
}: SigningPhaseCardProps) {
  const { classes } = useStyles();
  const isComplete =
    phase.workflow.state === Apollo.WorkflowState.Signed ||
    phase.workflow.state === Apollo.WorkflowState.Complete;
  const [expanded, setExpanded] = useState(!isComplete);

  useEffect(() => {
    if (isComplete) setExpanded(false);
  }, [isComplete]);

  const documentCount = phase.workflow.files.filter(
    f => f.documentRole === Apollo.WorkflowFileDocumentRole.Signing
  ).length;

  return (
    <WorkflowReadyOnlyContext.Provider value={isComplete || readOnly}>
      <div className={clsx(classes.root, isActive && classes.activeRoot)}>
        <div className={classes.header} role="button" tabIndex={0}>
          <Typography variant="subtitle1">Signing Phase</Typography>
          <Spacer />
          <SigningPhaseHeaderActions
            boardId={boardId}
            documentCount={documentCount}
            expanded={expanded}
            isActive={isActive}
            isComplete={isComplete}
            phase={phase}
            readOnly={readOnly}
            workflowColumnCategory={workflowColumnCategory}
            workflowId={workflowId}
            onToggle={() => setExpanded(prev => !prev)}
          />
        </div>
        <Collapse mountOnEnter in={expanded}>
          <SigningPhaseCardBody phase={phase} workflowId={workflowId} />
        </Collapse>
      </div>
    </WorkflowReadyOnlyContext.Provider>
  );
});

interface SigningPhaseHeaderActionsProps {
  boardId: string;
  documentCount: number;
  expanded: boolean;
  isActive: boolean;
  isComplete: boolean;
  onToggle: () => void;
  phase: ApolloTypes.PhaseSimple_SigningPhase_Fragment;
  readOnly: boolean;
  workflowColumnCategory: Apollo.ColumnCategory;
  workflowId: UUID;
}

function SigningPhaseHeaderActions({
  boardId,
  documentCount,
  expanded,
  isActive,
  isComplete,
  onToggle,
  phase,
  readOnly,
  workflowColumnCategory,
  workflowId,
}: SigningPhaseHeaderActionsProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { openDrawer } = useGlobalDrawer();
  const { exportToPdf, loading: exportLoading } = useExportWorkflowFiles(workflowId);

  if (isComplete) {
    return (
      <div className={classes.headerActions} onClick={e => e.stopPropagation()}>
        {(isActive || !expanded) && <PhaseStatusIndicator isCompleted isActive={isActive} />}
        <PhaseHomeColumnChip
          isSigningPhase
          boardId={boardId}
          isPhaseActive={isActive}
          isPhaseCompleted={isComplete}
          phase={phase}
          workflowColumnCategory={workflowColumnCategory}
        />
        <AlineTooltip arrow placement="top" title="Download documents">
          <span>
            <IconButton disabled={exportLoading} size="small" onClick={exportToPdf}>
              <DownloadIcon size={18} />
            </IconButton>
          </span>
        </AlineTooltip>
        <AlineTooltip arrow placement="top" title={expanded ? 'Collapse' : 'Expand'}>
          <IconButton size="small" onClick={onToggle}>
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </IconButton>
        </AlineTooltip>
      </div>
    );
  }

  return (
    <div className={classes.headerActions} onClick={e => e.stopPropagation()}>
      {(isActive || !expanded) && <PhaseStatusIndicator isActive={isActive} isCompleted={false} />}
      <PhaseHomeColumnChip
        isSigningPhase
        boardId={boardId}
        isPhaseActive={isActive}
        isPhaseCompleted={isComplete}
        phase={phase}
        workflowColumnCategory={workflowColumnCategory}
      />
      {!readOnly && (
        <AlineTooltip arrow placement="top" title="Signing settings">
          <IconButton
            aria-label="Signing settings"
            size="small"
            onClick={() => {
              openDrawer();
              navigate(link.WorkflowDetailTabPhaseSettings({ phaseId: phase.id, workflowId }));
            }}
          >
            <Settings size={18} />
          </IconButton>
        </AlineTooltip>
      )}
      <AlineTooltip
        arrow
        aria-label="Signing documents"
        placement="top"
        title={
          documentCount
            ? `${documentCount} signing document${documentCount > 1 ? 's' : ''}`
            : 'Add a signing document'
        }
      >
        <IconButton
          size="small"
          onClick={() =>
            navigate(link.WorkflowDetailTabDocuments({ workflowId }), { replace: true })
          }
        >
          <File size={16} />
        </IconButton>
      </AlineTooltip>
      {!readOnly && <DeletePhaseButton isSigningPhase phase={phase} workflow={phase.workflow} />}
      <AlineTooltip arrow placement="top" title={expanded ? 'Collapse' : 'Expand'}>
        <IconButton size="small" onClick={onToggle}>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </IconButton>
      </AlineTooltip>
    </div>
  );
}

interface SigningPhaseCardBodyProps {
  phase: ApolloTypes.PhaseSimple_SigningPhase_Fragment;
  workflowId: UUID;
}

function SigningPhaseCardBody({ phase, workflowId }: SigningPhaseCardBodyProps) {
  const { classes } = useStyles();
  const workflow = phase.workflow;
  const activeStep = phase.steps.find(step => !getIsWorkflowStepComplete(step)) ?? null;

  const shouldShowStepper =
    workflow.state !== Apollo.WorkflowState.Template &&
    workflow.state !== Apollo.WorkflowState.Signed &&
    workflow.state !== Apollo.WorkflowState.Complete;

  return (
    <div className={classes.content}>
      <SigningBlockingIssuesAlert workflowId={workflowId} />
      {shouldShowStepper && <SigningProgressStepper workflowState={workflow.state} />}
      <ManageRecipientsSection
        activeStep={activeStep ?? null}
        orderedSteps={phase.steps}
        workflow={workflow}
      />
    </div>
  );
}
